"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useId, useMemo, useRef, useState, useTransition } from "react";
import { cn } from "@local/utils/src/cn";
import { Flag } from "./flags";
import { marketsData } from "../../../lib/const/marketsData";
import { useOutsideClickCapture } from "@local/utils/src/useOutsideClickCapture";
import { CheckIcon } from "@local/ui/src/components/icons/Check";
import Cookies from "js-cookie";
import { I18n } from "../../../lib/i18n";
import { useRouter } from "next/navigation";
import type { Route } from "next";
import { trackAction } from "@local/tracking/src/trackActions";
import { ArrowDownIcon } from "@local/ui/src/components/ui/icons";
import { LoadingSpinner } from "@local/ui/src/LoadingSpinner";
export interface CountryOption {
  value: string;
  label: string;
  currencySymbol: string;
}

// Static market options - extracted outside component to prevent recreation on each render
const MARKET_OPTIONS: CountryOption[] = marketsData.map(country => ({
  value: country.market,
  label: country.name,
  currencySymbol: country.currencySymbol
}));
interface CountrySelectorDropdownProps {
  selected: string;
  lightBackground?: boolean;
  className?: string;
  wrapperClassName?: string;
  disabled?: boolean;
}
const FlagWrapper = t0 => {
  const $ = _c(2);
  const {
    country
  } = t0;
  let t1;
  if ($[0] !== country) {
    t1 = <div className="flex aspect-square items-center justify-center rounded-full bg-gray-200 p-1.5"><div className="flex h-[18px] w-[18px] items-center justify-center overflow-hidden rounded-full"><Flag country={country} className="h-full w-full object-cover" width={16} height={16} /></div></div>;
    $[0] = country;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};
export const MarketSelectorDropdown = t0 => {
  const $ = _c(46);
  const {
    selected,
    lightBackground: t1,
    className,
    wrapperClassName: t2,
    disabled: t3
  } = t0;
  const lightBackground = t1 === undefined ? false : t1;
  const wrapperClassName = t2 === undefined ? "" : t2;
  const disabled = t3 === undefined ? false : t3;
  const [isOpen, setIsOpen] = useState(false);
  const [isPending, startTransition] = useTransition();
  const uniqueId = useId();
  const wrapperRef = useRef(null);
  const {
    push
  } = useRouter();
  let t4;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => setIsOpen(false);
    $[0] = t4;
  } else {
    t4 = $[0];
  }
  useOutsideClickCapture(wrapperRef, t4);
  let t5;
  let t6;
  if ($[1] !== isOpen) {
    t5 = () => {
      if (!isOpen) {
        return;
      }
      const handleEscape = e => {
        if (e.key === "Escape") {
          setIsOpen(false);
        }
      };
      document.addEventListener("keydown", handleEscape);
      return () => document.removeEventListener("keydown", handleEscape);
    };
    t6 = [isOpen];
    $[1] = isOpen;
    $[2] = t5;
    $[3] = t6;
  } else {
    t5 = $[2];
    t6 = $[3];
  }
  useEffect(t5, t6);
  let t7;
  if ($[4] !== selected) {
    t7 = MARKET_OPTIONS.find(market => market.value === selected.toLowerCase());
    $[4] = selected;
    $[5] = t7;
  } else {
    t7 = $[5];
  }
  const selectedCountry = t7;
  let t8;
  if ($[6] !== push || $[7] !== selected) {
    t8 = countryCode => {
      const country_0 = marketsData.find(country => country.market === countryCode.toLowerCase());
      if (country_0) {
        Cookies.set("override_store", countryCode.toLowerCase());
        trackAction("market_selector_change", {
          from: selected.toLowerCase(),
          to: countryCode.toLowerCase()
        });
        const url = I18n.buildMarketUrl(window.location.href, selected.toLowerCase(), countryCode.toLowerCase());
        startTransition(() => {
          push(url.href as Route);
        });
      } else {
        console.error(`User tried to change to an invalid country: ${countryCode}`);
      }
    };
    $[6] = push;
    $[7] = selected;
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  const handleChange = t8;
  let t9;
  if ($[9] !== wrapperClassName) {
    t9 = cn("relative my-4 w-fit", wrapperClassName);
    $[9] = wrapperClassName;
    $[10] = t9;
  } else {
    t9 = $[10];
  }
  const t10 = disabled && "cursor-not-allowed opacity-60";
  let t11;
  if ($[11] !== className || $[12] !== t10) {
    t11 = cn("relative cursor-pointer rounded-full border-2 border-gray-200 bg-none py-2 pr-12 pl-4 text-left transition-all duration-200 focus:border-sky-500 focus:ring-2 focus:ring-sky-500 focus:outline-none", t10, className);
    $[11] = className;
    $[12] = t10;
    $[13] = t11;
  } else {
    t11 = $[13];
  }
  let t12;
  if ($[14] !== disabled || $[15] !== isOpen) {
    t12 = () => {
      if (!disabled) {
        setIsOpen(!isOpen);
      }
    };
    $[14] = disabled;
    $[15] = isOpen;
    $[16] = t12;
  } else {
    t12 = $[16];
  }
  let t13;
  if ($[17] !== selectedCountry) {
    t13 = selectedCountry && <FlagWrapper country={selectedCountry.value} />;
    $[17] = selectedCountry;
    $[18] = t13;
  } else {
    t13 = $[18];
  }
  const t14 = lightBackground ? "text-black" : "text-white";
  let t15;
  if ($[19] !== t14) {
    t15 = cn("font-body font-medium", t14);
    $[19] = t14;
    $[20] = t15;
  } else {
    t15 = $[20];
  }
  const t16 = `${selectedCountry?.label} (${selectedCountry?.currencySymbol})`;
  let t17;
  if ($[21] !== t15 || $[22] !== t16) {
    t17 = <span className={t15}>{t16}</span>;
    $[21] = t15;
    $[22] = t16;
    $[23] = t17;
  } else {
    t17 = $[23];
  }
  let t18;
  if ($[24] !== t13 || $[25] !== t17) {
    t18 = <span className="flex items-center gap-3">{t13}{t17}</span>;
    $[24] = t13;
    $[25] = t17;
    $[26] = t18;
  } else {
    t18 = $[26];
  }
  let t19;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3"><ArrowDownIcon className="text-black" aria-hidden="true" /></span>;
    $[27] = t19;
  } else {
    t19 = $[27];
  }
  let t20;
  if ($[28] !== disabled || $[29] !== isOpen || $[30] !== t11 || $[31] !== t12 || $[32] !== t18) {
    t20 = <button type="button" className={t11} disabled={disabled} aria-haspopup="listbox" aria-expanded={isOpen} aria-label="Select country" onClick={t12}>{t18}{t19}</button>;
    $[28] = disabled;
    $[29] = isOpen;
    $[30] = t11;
    $[31] = t12;
    $[32] = t18;
    $[33] = t20;
  } else {
    t20 = $[33];
  }
  let t21;
  if ($[34] !== isPending) {
    t21 = isPending && <div className="absolute inset-0 z-50 flex items-center justify-center rounded-full bg-white/80"><LoadingSpinner className="-mt-3 h-5 w-5" /></div>;
    $[34] = isPending;
    $[35] = t21;
  } else {
    t21 = $[35];
  }
  let t22;
  if ($[36] !== handleChange || $[37] !== isOpen || $[38] !== selected || $[39] !== uniqueId) {
    t22 = isOpen && <div role="listbox" aria-label="Select country" className="absolute bottom-0 z-50 mt-1 max-h-60 w-max overflow-auto border border-gray-200 bg-white shadow-lg"><ul className="py-1">{MARKET_OPTIONS.map(market_0 => {
          const isSelected = selected === market_0.value;
          const isDisabled = isSelected;
          return <li key={`market-option-${market_0.value}-${uniqueId}`}><button type="button" role="option" aria-selected={isSelected} disabled={isDisabled} className={cn("relative flex w-full cursor-pointer items-center gap-3 py-3 pr-12 pl-4 text-sm transition-colors duration-150 select-none hover:bg-gray-100", isDisabled ? "cursor-not-allowed bg-gray-100 text-gray-400" : isSelected ? "bg-sky-50 text-sky-900" : "text-gray-900")} onClick={() => {
              if (!isDisabled) {
                handleChange(market_0.value);
                setIsOpen(false);
              }
            }}><FlagWrapper country={market_0.value} /><span className="font-body flex-1 text-start font-medium">{market_0.label} ({market_0.currencySymbol})</span>{isSelected && <span className="absolute right-4 flex items-center text-sky-600"><CheckIcon className="h-5 w-5" aria-hidden="true" /></span>}</button></li>;
        })}</ul></div>;
    $[36] = handleChange;
    $[37] = isOpen;
    $[38] = selected;
    $[39] = uniqueId;
    $[40] = t22;
  } else {
    t22 = $[40];
  }
  let t23;
  if ($[41] !== t20 || $[42] !== t21 || $[43] !== t22 || $[44] !== t9) {
    t23 = <div className={t9} ref={wrapperRef}>{t20}{t21}{t22}</div>;
    $[41] = t20;
    $[42] = t21;
    $[43] = t22;
    $[44] = t9;
    $[45] = t23;
  } else {
    t23 = $[45];
  }
  return t23;
};